import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Search, Trash2 } from "lucide-react";
import { deleteContact, getContacts } from "../../../services/admin/contact";

interface Contact {
    id: number;
    name: string;
    email: string;
    company?: string;
    phone?: string;
    message?: string;
}

function AdminContact() {
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [search, setSearch] = useState("");
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const loadContacts = async () => {
        setIsLoading(true);
        const res = await getContacts();
        if (res.success && Array.isArray(res.data)) {
            setContacts(res.data);
        } else {
            console.error("Failed to load contacts", res.error || res.data);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadContacts();
    }, []);

    const handleDeleteClick = (contact: Contact) => {
        setSelectedContact(contact);
        setShowConfirm(true);
    };

    const confirmDelete = async () => {
        if (selectedContact) {
            const res = await deleteContact(selectedContact.id);
            if (res.success) {
                setContacts((prev) => prev.filter((c) => c.id !== selectedContact.id));
            } else {
                alert("Failed to delete contact: " + (res.error || "Unknown error"));
            }
            setShowConfirm(false);
            setSelectedContact(null);
        }
    };

    const filteredContacts = contacts.filter((contact) =>
        contact.name.toLowerCase().includes(search.toLowerCase())
    );

    const columns: TableColumn<Contact>[] = [
        {
            name: "ID",
            cell: (_row, index) => (index + 1).toString(),
            width: "60px",
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Email",
            selector: (row) => row.email,
            sortable: true,
        },
        {
            name: "Phone",
            selector: (row) => row.phone || "-",
        },
        {
            name: "Company",
            selector: (row) => row.company || "-",
        },
        {
            name: "Message",
            selector: (row) => row.message || "-",
            wrap: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <button
                    onClick={() => handleDeleteClick(row)}
                    className="text-red-600 hover:text-red-800"
                    title="Delete"
                >
                    <Trash2 size={16} />
                </button>
            ),
            ignoreRowClick: true,
        },
    ];

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-semibold text-gray-800">Contact Submissions</h1>

            {/* Search */}
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search contact..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#009FE3]"
                    />
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow border">
                <DataTable
                    columns={columns}
                    data={filteredContacts}
                    progressPending={isLoading}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    noHeader
                />
            </div>

            {/* Confirm Delete Modal */}
            {showConfirm && selectedContact && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
                        <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
                        <p className="mb-4">
                            Are you sure you want to delete{" "}
                            <strong>{selectedContact.name}</strong>'s submission?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowConfirm(false);
                                    setSelectedContact(null);
                                }}
                                className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminContact;
